import { useRef, useMemo, useEffect, useLayoutEffect, useState } from "react"
import { useVirtualizer } from "@tanstack/react-virtual"
import { useLocation } from "react-router-dom"
import { useMainScroll } from "@/app/providers/MainScrollProvider"
import type { ViewItem } from "@/features/views/hooks/useViewData"
import type { SwipeAction } from "@/features/tasks/components/SwipeableTaskRow"
import { ObjectType } from "@/db/schema"
import { getSavedScroll } from "@/shared/hooks/useScrollRestore"
import { SortableTaskRow } from "./SortableTaskRow"
import { DndListProvider } from "./DndListProvider"
import { computeGroupPositions } from "@/features/views/utils/groupPositions"

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ROW_HEIGHT = 44
const SECTION_ROW_HEIGHT = 82
const OVERSCAN = 15
/** Use virtualizer above this threshold */
const VIRTUALIZE_THRESHOLD = 50

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DndTaskListProps {
  /** ViewItems in display order — contains task + correct sortIndex per view */
  items: ViewItem[]
  /** Which index field to persist: "index" (container) or "startAtIndex" (date views) */
  indexField: "index" | "startAtIndex"
  /** Optimistic setter — splices the reordered array for instant feedback */
  setOptimistic: (
    updater:
      | ViewItem[]
      | ((prev: ViewItem[] | null) => ViewItem[] | null)
      | null,
  ) => void
  /** Called on complete swipe */
  onComplete: (id: string) => void
  /** Called on uncomplete swipe */
  onUncomplete: (id: string) => void
  /** Called for non-completion swipe actions */
  onSwipeAction?: (id: string, action: SwipeAction) => void
  /** Currently active (expanded) task ID — from useActiveTask */
  activeTaskId?: string | null
  /** Whether the active task title is being edited */
  isEditingTitle?: boolean
  /** Open task detail for a given ID */
  onOpenTask?: (id: string) => void
  /** Close active task detail */
  onCloseTask?: () => void
  /** Skip ghost-index writes (flat Today mode) — only write primary index */
  skipGhostIndex?: boolean
}

// ---------------------------------------------------------------------------
// DndTaskList
// ---------------------------------------------------------------------------

/**
 * Flat sortable list — renders SortableTaskRows wrapped in DndListProvider.
 *
 * Supports two rendering modes:
 * - **Virtualized** (>50 items): @tanstack/react-virtual with absolute positioning
 * - **Simple** (≤50 items): normal DOM flow
 *
 * All DnD context (sensors, overlay, reorder logic) is delegated to
 * DndListProvider so that grouped views share the exact same behavior.
 */
export function DndTaskList({
  items,
  indexField,
  setOptimistic,
  onComplete,
  onUncomplete,
  onSwipeAction,
  activeTaskId,
  isEditingTitle,
  onOpenTask,
  onCloseTask,
  skipGhostIndex,
}: DndTaskListProps) {
  // Expanded card MUST be in normal document flow for the margin animation
  // to push siblings (v1 pattern). Virtualized rows are position:absolute which
  // prevents this, so we fall back to SimpleSortableList when a task is open.
  const useVirtual = items.length > VIRTUALIZE_THRESHOLD && !activeTaskId

  // ---------------------------------------------------------------------------
  // Track which task just closed so the remounted TaskItem can animate.
  // When switching simple→virtual, the activeTaskId that was set becomes the
  // closingTaskId. Auto-cleared after the animation duration (2.1s).
  // ---------------------------------------------------------------------------
  const prevActiveRef = useRef(activeTaskId)
  const [closingTaskId, setClosingTaskId] = useState<string | null>(null)

  useEffect(() => {
    if (prevActiveRef.current && !activeTaskId) {
      setClosingTaskId(prevActiveRef.current)
    }
    prevActiveRef.current = activeTaskId
  }, [activeTaskId])

  // Clear closingTaskId after the close animation completes
  useEffect(() => {
    if (!closingTaskId) return
    const timer = setTimeout(() => setClosingTaskId(null), 2100)
    return () => clearTimeout(timer)
  }, [closingTaskId])

  // ---------------------------------------------------------------------------
  // Scroll preservation across Virtual ↔ Simple switching
  // ---------------------------------------------------------------------------
  const { scrollRef } = useMainScroll()
  const { pathname } = useLocation()
  // Initialize from nav scroll cache so the virtualizer starts at the right offset
  const scrollPosRef = useRef(getSavedScroll(pathname))
  const prevUseVirtualRef = useRef(useVirtual)

  // Continuously track scroll position so we have the latest value when switching
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const handler = () => {
      scrollPosRef.current = el.scrollTop
    }
    el.addEventListener("scroll", handler, { passive: true })
    return () => el.removeEventListener("scroll", handler)
  }, [scrollRef])

  // Restore scroll position synchronously before paint when switching back to virtual
  useLayoutEffect(() => {
    if (useVirtual && !prevUseVirtualRef.current && scrollRef.current) {
      scrollRef.current.scrollTop = scrollPosRef.current
    }
    prevUseVirtualRef.current = useVirtual
  }, [useVirtual, scrollRef])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const { firsts, lasts } = useMemo(() => computeGroupPositions(items), [items])

  return (
    <DndListProvider
      items={items}
      indexField={indexField}
      setOptimistic={setOptimistic}
      skipGhostIndex={skipGhostIndex}
    >
      {useVirtual ? (
        <VirtualSortableList
          items={items}
          onComplete={onComplete}
          onUncomplete={onUncomplete}
          onSwipeAction={onSwipeAction}
          activeTaskId={activeTaskId}
          isEditingTitle={isEditingTitle}
          onOpenTask={onOpenTask}
          onCloseTask={onCloseTask}
          groupFirsts={firsts}
          groupLasts={lasts}
          initialScrollOffset={scrollPosRef.current}
          closingTaskId={closingTaskId}
        />
      ) : (
        <SimpleSortableList
          items={items}
          onComplete={onComplete}
          onUncomplete={onUncomplete}
          onSwipeAction={onSwipeAction}
          activeTaskId={activeTaskId}
          isEditingTitle={isEditingTitle}
          onOpenTask={onOpenTask}
          onCloseTask={onCloseTask}
          groupFirsts={firsts}
          groupLasts={lasts}
          closingTaskId={closingTaskId}
        />
      )}
    </DndListProvider>
  )
}

// ---------------------------------------------------------------------------
// Simple (non-virtualized) sortable list
// ---------------------------------------------------------------------------

interface SortableListInnerProps {
  items: ViewItem[]
  onComplete: (id: string) => void
  onUncomplete: (id: string) => void
  onSwipeAction?: (id: string, action: SwipeAction) => void
  activeTaskId?: string | null
  isEditingTitle?: boolean
  onOpenTask?: (id: string) => void
  onCloseTask?: () => void
  groupFirsts: boolean[]
  groupLasts: boolean[]
  /** Task that just closed — triggers close animation on remount after virtual↔simple switch */
  closingTaskId?: string | null
}

function SimpleSortableList({
  items,
  onComplete,
  onUncomplete,
  onSwipeAction,
  activeTaskId,
  isEditingTitle,
  onOpenTask,
  onCloseTask,
  groupFirsts,
  groupLasts,
  closingTaskId,
}: SortableListInnerProps) {
  return (
    <div className="flex flex-col">
      {items.map((vi, idx) => {
        const taskId = vi.task.id ?? "no-id"
        const isActive = taskId === activeTaskId
        return (
          <SortableTaskRow
            key={taskId}
            item={vi}
            onComplete={onComplete}
            onUncomplete={onUncomplete}
            onSwipeAction={onSwipeAction}
            isActive={isActive}
            isClosing={taskId === closingTaskId}
            editTitle={isActive && !!isEditingTitle}
            onOpen={onOpenTask}
            onClose={onCloseTask}
            isFirstInGroup={groupFirsts[idx]}
            isLastInGroup={groupLasts[idx]}
          />
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Virtualized sortable list
// ---------------------------------------------------------------------------

function VirtualSortableList({
  items,
  onComplete,
  onUncomplete,
  onSwipeAction,
  activeTaskId,
  isEditingTitle,
  onOpenTask,
  onCloseTask,
  groupFirsts,
  groupLasts,
  initialScrollOffset,
  closingTaskId,
}: SortableListInnerProps & { initialScrollOffset?: number; closingTaskId?: string | null }) {
  const { scrollRef } = useMainScroll()
  const listRef = useRef<HTMLDivElement>(null)

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) => {
      const vi = items[index]
      if (vi && vi.task.id === activeTaskId) return 220
      if (vi && vi.task.type === ObjectType.Section) return SECTION_ROW_HEIGHT
      return ROW_HEIGHT
    },
    overscan: OVERSCAN,
    scrollMargin: listRef.current?.offsetTop ?? 0,
    initialOffset: initialScrollOffset,
  })

  return (
    <div ref={listRef}>
      <div
        className="relative w-full"
        style={{ height: virtualizer.getTotalSize() }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const vi = items[virtualRow.index]
          if (!vi) return null
          const taskId = vi.task.id ?? `v-${virtualRow.index}`
          const isActive = vi.task.id === activeTaskId

          return (
            <SortableTaskRow
              key={taskId}
              item={vi}
              virtualStart={virtualRow.start}
              virtualSize={isActive ? undefined : virtualRow.size}
              scrollMargin={virtualizer.options.scrollMargin}
              measureRef={virtualizer.measureElement}
              virtualIndex={virtualRow.index}
              onComplete={onComplete}
              onUncomplete={onUncomplete}
              onSwipeAction={onSwipeAction}
              isActive={isActive}
              isClosing={taskId === closingTaskId}
              editTitle={isActive && !!isEditingTitle}
              onOpen={onOpenTask}
              onClose={onCloseTask}
              isFirstInGroup={groupFirsts[virtualRow.index]}
              isLastInGroup={groupLasts[virtualRow.index]}
            />
          )
        })}
      </div>
    </div>
  )
}
